import styled, { css } from 'styled-components';
import { fontSize, fontWeight } from 'modules/theme';
import { pxToRem } from 'modules/v2/foundation/spacing';
import { themeGet } from 'modules/v2/foundation/theme';
const disabledStyle = css `
  box-shadow: none;
  border: ${pxToRem(1)} dashed ${themeGet('colors.disabled.200')};
  margin: ${pxToRem(1)}; /* /* prevents the input border from jumping */
`;
const defaultStyle = css `
  background-color: ${themeGet('colors.background.50')};
  border-color: ${themeGet('colors.border.100')};

  &:hover:not(:has(input:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.border.100')};
    border-color: ${themeGet('colors.border.100')};
  }

  &:has(input:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.border.300')};
    border-color: ${themeGet('colors.border.300')};

    [data-icon='falcon'] {
      color: ${themeGet('colors.primary.1000')};
    }
  }

  &:has(input:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.primary.1000')};
    border-color: ${themeGet('colors.primary.1000')};
  }

  &:has(input:disabled) {
    ${disabledStyle};
  }
`;
const invalidStyle = css `
  background-color: ${themeGet('colors.background.50')};
  border-color: ${themeGet('colors.text.danger')};

  &:hover:not(:has(input:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.text.danger')};
    border-color: ${themeGet('colors.text.danger')};
  }

  &:has(input:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.text.danger')};
    border-color: ${themeGet('colors.text.danger')};

    [data-icon='falcon'] {
      color: ${themeGet('colors.text.danger')};
    }
  }

  &:has(input:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.text.danger')};
    border-color: ${themeGet('colors.text.danger')};
  }

  &:has(input:disabled) {
    ${disabledStyle};
  }
`;
const highlightStyle = css `
  background-color: ${themeGet('colors.blue.50')};
  border-color: ${themeGet('colors.blue.500')};

  &:hover:not(:has(input:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.blue.500')};
    border-color: ${themeGet('colors.blue.500')};
  }

  &:has(input:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.blue.500')};
    border-color: ${themeGet('colors.blue.500')};

    [data-icon='falcon'] {
      color: ${themeGet('colors.blue.500')};
    }
  }

  &:has(input:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.blue.500')};
    border-color: ${themeGet('colors.blue.500')};
  }

  &:has(input:disabled) {
    ${disabledStyle};
  }
`;
const inputAppearance = (props) => {
    if (props.$invalid) {
        return invalidStyle;
    }
    switch (props.$appearance) {
        case 'highlight':
            return highlightStyle;
        default:
            return defaultStyle;
    }
};
export const Input = styled.input `
  border: none;
  background-color: transparent;
  flex: 1 1 100%;
  font-size: ${themeGet('fontSize.medium')};
  line-height: ${themeGet('lineHeight.default')};
  color: ${themeGet('colors.text.300')};

  &:focus-visible {
    outline: none;
  }

  &:disabled {
    color: ${themeGet('colors.disabled.200')};
  }
`;
export const Container = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${themeGet('spacing.sm')};
  position: relative;
  width: ${(props) => props.$stretch && '100%;'};
`;
export const Adornment = styled.div `
  display: flex;
`;
const descriptionInvalidStyles = css `
  color: ${themeGet('colors.text.danger')};
`;
export const Description = styled.div `
  ${fontSize('tiny')};
  ${fontWeight('normal')};
  color: ${themeGet('colors.text.300')};
  display: inline-block;
  margin-top: ${themeGet('spacing.sm')};
  line-height: ${themeGet('lineHeight.neutralized')};

  &:has(+ * input:disabled) {
    color: ${themeGet('colors.disabled.200')};
  }

  &:has(+ * input:focus-visible) {
    color: ${(props) => !props.$invalid && themeGet('colors.primary.1000')};
  }

  ${(props) => props.$invalid && descriptionInvalidStyles};
`;
export const InputContainer = styled.div `
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: ${themeGet('spacing.lg')};

  padding: ${pxToRem(9)} ${pxToRem(16)};
  height: ${pxToRem(40)};
  line-height: ${pxToRem(40)};

  border: ${pxToRem(1)} solid transparent;
  border-radius: ${themeGet('borderRadius.md')};
  white-space: nowrap;

  ${(props) => inputAppearance(props)};

  transition:
    box-shadow,
    border-color,
    0.25s ease-in-out;
`;
